import React, { useCallback, useMemo } from 'react';
import { arrayOf, bool, node, string } from 'prop-types';
import classNames from 'classnames';
import { propTypes } from '../../../util/types';
import {
  ListingCard,
  PaginationLinks,
  SecondaryButton,
  SectionExperience,
  NamedLink,
} from '../../../components';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parse, stringify } from '../../../util/urlHelpers';
import { createURLParamsForListing } from '../../../util/listingHelpers';
import { LOCALE_TO_LANG } from '../../../constants/locales';
import ListingService from '../../../services/ListingService';

import css from './SearchResultsPanel.module.css';
import { useSelector } from 'react-redux';
import { useConfiguration } from '../../../context/configurationContext';

// Helper to get localized listing title for SEO (using preloaded translations)
const getLocalizedListingTitle = (listing, language, intl) => {
  const publicData = listing?.attributes?.publicData;
  const discipline = publicData?.discipline;
  const cityName = publicData?.multiLangAddressParts?.[language]?.city;
  const authorName = listing?.author?.attributes?.profile?.displayName;
  
  // Get translated discipline from preloaded messages (no intl.formatMessage needed)
  const translatedDiscipline = discipline 
    ? ListingService.generalMessages[language]?.[`General.${discipline}`] || discipline
    : null;
  
  // Get translated "in" word
  const inText = ListingService.generalMessages[language]?.['General.in'] || 'in';
  
  // Get translated "with" word
  const withText = ListingService.generalMessages[language]?.['General.with'] || 'with';
  
  // Build: "Ski Alpino in Milano with John Doe"
  if (translatedDiscipline && cityName && authorName) {
    return `${translatedDiscipline} ${inText} ${cityName} ${withText} ${authorName}`;
  }
  
  // Fallback to default title
  return listing?.attributes?.title;
};

const panelMediumWidth = 50;
const panelLargeWidth = 62.5;
const cardRenderSizes = [
  '(max-width: 767px) 100vw',
  `(max-width: 1023px) ${panelMediumWidth}vw`,
  `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
  `${panelLargeWidth / 3}vw`,
].join(', ');

const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings,
    pagination,
    search,
    suggestedListings,
    searchInProgress,
    setActiveListing,
    onLoadMoreListings,
  } = props;

  const { currentUser } = useSelector(state => state.user);
  const { interfaceLang } = useSelector(state => state.ui);
  const config = useConfiguration();
  const intl = useIntl();

  // Get language for localization (works in SSR)
  const language = LOCALE_TO_LANG[interfaceLang] || 'english';

  const { currentPage, totalPages } = pagination || {};

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);

  const shouldShowLoadMore = useMemo(
    () => listings.length !== 0 && (searchInProgress || totalPages > currentPage),
    [listings.length, currentPage, totalPages, searchInProgress]
  );

  const hasSuggestedListings = useMemo(() => suggestedListings.length > 0, [
    suggestedListings.length,
  ]);

  const queryParams = parse(search);

  const hasPageQueryParam = queryParams.page > 0;

  const loadMore = useCallback(() => {
    const searchPaginated = stringify({ ...queryParams, page: currentPage + 1 });

    onLoadMoreListings(searchPaginated);
  }, [queryParams, currentPage, onLoadMoreListings]);

  return (
    <div className={classes}>
      {/* Hidden navigation links for SEO crawlers - using sr-only pattern */}
      {listings.length > 0 && (
        <nav className="sr-only" style={{ 
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}>
          <ul>
            {listings.map(l => {
              // Generate localized title and URL params using Redux state (SSR-compatible)
              const localizedTitle = getLocalizedListingTitle(l, language, intl);
              const urlParams = createURLParamsForListing(l, interfaceLang, intl);
              return (
                <li key={l.id.uuid}>
                  <NamedLink name="ListingPage" params={urlParams}>
                    {localizedTitle}
                  </NamedLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
      
      {listings.map(l => (
        <ListingCard
          className={css.listingCard}
          key={l.id.uuid}
          id={`listing-${l.id.uuid}`}
          listing={l}
          renderSizes={cardRenderSizes}
          setActiveListing={setActiveListing}
          showOnlyAverageReview
          currentUser={currentUser}
          config={config}
        />
      ))}
      {shouldShowLoadMore && !hasPageQueryParam && (
        <SecondaryButton
          className={css.paginationContainer}
          disabled={searchInProgress}
          inProgress={searchInProgress}
          onClick={loadMore}
        >
          <FormattedMessage id="General.loadMore" />
        </SecondaryButton>
      )}

      {hasSuggestedListings ? (
        <>
          <div className={css.suggestedListings}>
            <FormattedMessage id="SearchPage.suggestedListings" />
          </div>
          
          {/* Hidden navigation links for suggested listings - SEO */}
          <nav className="sr-only" style={{ 
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}>
            <ul>
              {suggestedListings.map(l => {
                // Generate localized title and URL params using Redux state (SSR-compatible)
                const localizedTitle = getLocalizedListingTitle(l, language, intl);
                const urlParams = createURLParamsForListing(l, interfaceLang, intl);
                return (
                  <li key={l.id.uuid}>
                    <NamedLink name="ListingPage" params={urlParams}>
                      {localizedTitle}
                    </NamedLink>
                  </li>
                );
              })}
            </ul>
          </nav>
          
          {suggestedListings.map(l => (
            <ListingCard
              className={css.listingCard}
              key={l.id.uuid}
              id={`listing-${l.id.uuid}`}
              listing={l}
              renderSizes={cardRenderSizes}
              setActiveListing={setActiveListing}
              showOnlyAverageReview
              currentUser={currentUser}
              config={config}
            />
          ))}
        </>
      ) : null}

      {pagination ? (
        <PaginationLinks
          className={classNames(css.pagination, { [css.paginationVisible]: hasPageQueryParam })}
          pageName="SearchPage"
          pageSearchParams={queryParams}
          pagination={pagination}
        />
      ) : null}

      {!searchInProgress && <SectionExperience className={css.contactBanner} />}
    </div>
  );
};

SearchResultsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  listings: [],
  pagination: null,
  searchInProgress: false,
  search: null,
};

SearchResultsPanel.propTypes = {
  className: string,
  rootClassName: string,
  children: node,
  listings: arrayOf(propTypes.listing),
  pagination: propTypes.pagination,
  searchInProgress: bool,
  search: string,
};

export default SearchResultsPanel;
